import { defineStore } from 'pinia'
import type { Depot, StockItem } from '@/data/schema/stocktake'
import StockTakeService from '@/services/stocktake'
import useToasterStore from '@/stores/toaster'
import type { UpdateType, PendingEntry, QueuedAction } from '@/data/schema/stocktake';
import offlineService from '@/services/offline';
export default defineStore('stocktake', {
  state: () => ({
    items: [] as StockItem[],
    totalItems: 0,  // Add this
    currentPage: 1,
    pageSize: 25,
    depots: [] as Depot[],
    subGroups: [],
    pGroups: [],
    statuses: ["0", "1", "2", "4","5"] as string[],
    areas: [],
    counter1: '' as string,
    counter2: '' as string,
    enteredArea: '' as string,
    selectedDepot: {
                      name: '' as string,
                      prefix: ''  as string   
                    },



    filters: {
      search: '',
      selectedSubGroup: '' as string,
      selectedArea: '' as string,
      selectedPGroup: '' as string,
      selectedStatus: '' as string,
    },

    sortedColumn: null as keyof StockItem | null,
    sortAsc: true as boolean,

    isloading: false as boolean,
    error: '' as string | '',
    version: '1.1' as string,
    showVariance: false as boolean,

    hasSubmitted: false as boolean,

    _timerId: null as ReturnType<typeof setInterval> | null,
    _countdownId: null as number | ReturnType<typeof setInterval> | null,
    pollIntervalMs: 60_000,
    countdownSeconds: 60,
    isPollingPaused: false,
    isFormOpen: false,
    pendingById: {} as Record<string | number, PendingEntry>,

    isOnline: true as boolean,
    isSyncing: false as boolean,
    queueLength: 0 as number,
    syncingItemIds: new Set<string | number>(),
    newOfflineItemIds: new Set<string | number>(),
  }),

  getters: {
    isCounter1Valid: (s) => s.counter1.trim().length > 0,
    isCounter2Valid: (s) => s.counter2.trim().length > 0,
    isAreaValid: (s) => s.enteredArea.trim().length > 0,
    isDepotValid: (s) => s.selectedDepot.name.trim().length > 0,

    isFormValid(): boolean {
      return this.isCounter1Valid && this.isCounter1Valid && this.isAreaValid && this.isDepotValid
    },

    counter1Error(): string {
      if (!this.isCounter1Valid && this.hasSubmitted) return 'First counter name is required.'
      return ''
    },
    counter2Error(): string {
      if (!this.isCounter2Valid && this.hasSubmitted) return 'Second counter name is required.'
      return ''
    },
    areaError(): string {
      if (!this.isAreaValid && this.hasSubmitted) return 'Area is required.'
      return ''
    },

    depotError(): string {
      if (!this.selectedDepot.name && this.hasSubmitted) return 'Depot must be selected.'
      return ''
    },


    validationErrors(): string[] {
      return [this.counter1Error, this.counter2Error, this.areaError, this.depotError].filter(
        (e) => e !== '',
      )
    },


    isPending: (state) => (id: string | number, type: UpdateType) =>
      !!state.pendingById[id]?.[type],

    isSyncingItem: (state) => (id: string | number) =>
      state.syncingItemIds.has(id),

    isNewOfflineItem: (state) => (id: string | number) =>
      state.newOfflineItemIds.has(id),
  },

  actions: {

    async loadItems(force = false, page?: number, pageSize?: number, sortBy?: string, sortType?: string) {
      const toaster = useToasterStore()
      const api = new StockTakeService()

      if (this.isloading && !force) return
      if (!this.isOnline && !force) return

      this.error = ''
      this.isloading = true

      
      if (page !== undefined) {
          this.currentPage = page
        }

      if (pageSize !== undefined) {
          this.pageSize = pageSize
        }

      if (sortBy !== undefined) {
          this.sortedColumn = sortBy as keyof StockItem
        }
      
      if (sortType !== undefined) {
          this.sortAsc = sortType === 'asc'
        }

    
      try {
        
        const depot =this.selectedDepot.prefix.toUpperCase();
        
        const params = new URLSearchParams()

        params.set('page', String(this.currentPage??1))
        params.set('per_page', String(this.pageSize??25))

        if (this.sortedColumn) params.set('sort_by', String(this.sortedColumn))
        if (this.sortAsc !== undefined && this.sortedColumn) params.set('sort_type', this.sortAsc ? 'asc' : 'desc')

        if (this.filters.search) params.set('search', this.filters.search)
        if (this.filters.selectedSubGroup) params.set('subGroup', this.filters.selectedSubGroup)
        if (this.filters.selectedArea) params.set('area', this.filters.selectedArea)
        if (this.filters.selectedPGroup) params.set('pgroup', this.filters.selectedPGroup)
        if (this.filters.selectedStatus) params.set('status', this.filters.selectedStatus)

        const search = params.toString()
        const response = await api.getStockitems(depot, search)

        // Server returns fresh page data - just replace items
        this.items = response.data.data

        this.totalItems = response.data.meta.total
        
        // Update filter options
        this.subGroups = response.data.subGroups
        this.areas = response.data.areas
        this.pGroups = response.data.pgroups
        

         this.switchVariance()

      } catch (e: any) {
        this.error = e?.message ?? 'Failed to fetch stock items'
        toaster.error({ text: 'Failed to fetch stock items' })
        throw e
      } finally {
        this.isloading = false
      }
    },

    startPolling() {
      if (this._timerId || this.isloading) return

      // Don't start polling if offline
      if (!this.isOnline) return

      void this.loadItems()
      this.isPollingPaused = false
      this.countdownSeconds = this.pollIntervalMs / 1000
      
      // Use requestAnimationFrame for smoother countdown updates
      let lastUpdateTime = Date.now()
      const updateCountdown = () => {
        if (this._countdownId === null) return
        
        const now = Date.now()
        if (now - lastUpdateTime >= 1000) {
          if (!this.isPollingPaused && this.countdownSeconds > 0) {
            this.countdownSeconds--
          } else if (this.countdownSeconds === 0) {
            this.countdownSeconds = this.pollIntervalMs / 1000
          }
          lastUpdateTime = now
        }
        
        if (this._countdownId !== null) {
          this._countdownId = requestAnimationFrame(updateCountdown)
        }
      }
      
      this._countdownId = requestAnimationFrame(updateCountdown)
      
      this._timerId = setInterval(() => {
        if (!this.isPollingPaused && this.isOnline && !this.isloading) {
          // Use idle callback for non-blocking processing
          if ('requestIdleCallback' in window) {
            requestIdleCallback(() => void this.loadItems(), { timeout: 2000 })
          } else {
            // Fallback for browsers without idle callback
            setTimeout(() => void this.loadItems(), 0)
          }
          this.countdownSeconds = this.pollIntervalMs / 1000
        }
      }, this.pollIntervalMs)
    },

    stopPolling() {
      if (this._timerId) {
        clearInterval(this._timerId)
        this._timerId = null
      }
      if (this._countdownId) {
        // Handle both setInterval and requestAnimationFrame
        if (typeof this._countdownId === 'number') {
          cancelAnimationFrame(this._countdownId)
        } else {
          clearInterval(this._countdownId)
        }
        this._countdownId = null
      }
    },

    togglePolling() {
      if (this.isPollingPaused) {
        this.resumePolling()
      } else {
        this.pausePolling()
      }
    },

    pausePolling() {
      this.isPollingPaused = true
    },

    resumePolling() {
      this.isPollingPaused = false
      this.countdownSeconds = this.pollIntervalMs / 1000
    },

    setFormOpen(isOpen: boolean) {
      this.isFormOpen = isOpen
    },

    async updateStockItem(depot: string, id: string, item: Partial<StockItem>) {
      // const toaster = useToasterStore()
      try {
        const api = new StockTakeService()

        const response = await api.updateStockItem(depot, id, item)

        return response
      } catch (e: any) {
        const message = e?.response?.data?.message || e?.message || 'Unable to update Items.'

        // Don't set global error for 409 conflicts - these are expected during concurrent updates
        if (e?.response?.status !== 409) {
          this.error = message
        }

        // Don't show toaster here - let the calling component handle it
        throw e
      } finally {
        this.isloading = false
      }
    },
    async duplicateStockItem(depot: string, item: Partial<StockItem>) {
      const toaster = useToasterStore()
      try {
        const api = new StockTakeService()

        const response = await api.addStockItem(depot, item)

        await this.loadItems(true)

        return response
      } catch (e: any) {
        this.error = e?.message ?? 'Unable to add Item.'
        toaster.error({
          text: 'Unable to add Item.',
        })
      } finally {
        this.isloading = false
      }
    },

    async createStockItem(depot: string, item: StockItem) {
      const toaster = useToasterStore()
      this.error = ''

      try {
        const api = new StockTakeService()

        const response = await api.addStockItem(depot, item)

        return response
      } catch (e: any) {
        this.error = e?.message ?? 'Unable to create New Item.'

        toaster.error({
          text: e?.message ?? 'Unable to create New Item.',
        })

        throw e
      } finally {
        this.isloading = false
      }
    },
    addItem(item: StockItem) {
      const depot = this.selectedDepot.prefix.toUpperCase()

      const withoutNulls = {
        ...Object.fromEntries(
          Object.entries(item).filter(([k, v]) => v !== null && k !== 'id' && k !== 'STACKNO'),
        ),
        Status: '1',
        AREA: this.enteredArea,
      }

      const newItem = this.duplicateStockItem(String(depot), withoutNulls)

      return newItem
    },
    changeArea(newArea: string) 
    {
      this.enteredArea = newArea
      this.updateSession()
    },

    updateSession() {
      const session = {
        counter1: this.counter1,
        counter2: this.counter2,
        area: this.enteredArea,
        depot: this.selectedDepot.name,
      }
      sessionStorage.setItem('stocktake-session', JSON.stringify(session))
    },


    applyLocalQty({ id, qty, type }: { id: string | number; qty: number; type: UpdateType }) {
      const item = this.items.find(i => i.id === id);
      if (!item) return;

      if (type === 'count') item.Quantity = qty;
      else item.Recount = qty;
    },

    markPending({ id, type }: { id: string | number; type: UpdateType }) {
      const entry = this.pendingById[id] ?? {};
      entry[type] = true;
      this.pendingById[id] = entry;
    },

    clearPending({ id, type }: { id: string | number; type: UpdateType }) {
      const entry = this.pendingById[id];
      if (!entry) return;
      delete entry[type];
      if (!entry.count && !entry.recount) delete this.pendingById[id];
    },

    replaceItem(item: any) {
      const idx = this.items.findIndex(i => i.id === item.id);
      if (idx >= 0) this.items[idx] = item;
    },
    ////depot

    async loadDepots(force = false) {
      if (this.isloading && !force) return
      const toaster = useToasterStore()

      this.isloading = true
      this.error = ''

      try {
        const api = new StockTakeService()


        const response = await api.getDepots()

        this.depots = response.data

      } catch (e: any) {
        this.error = e?.message ?? 'Unable to load depots.'
        toaster.error({
          text: 'Unable to load depots.',
        })
      } finally {
        this.isloading = false
      }
    },

     setSelectedDepot(depot:string) {
       
      const currentDepot = this.depots.find(d => d.name === depot)
  

        if (!currentDepot && depot.length > 0) {
            this.selectedDepot = {
              name: depot,
              prefix: depot.substring(0, 3)
            }          
        } else {
          this.selectedDepot = {
            name: currentDepot.name,
            prefix: currentDepot.prefix
          }
        }        

    },

    // session actions

    attemptSubmit(): boolean {
      this.hasSubmitted = true

      return this.isFormValid
    },

    resetValidation(): void {
      this.hasSubmitted = false
    },

    startSession(data: { counter1: string; counter2: string; area: string; depot: string }): void {
      this.counter1 = data.counter1
      this.counter2 = data.counter2
      this.enteredArea = data.area
      this.selectedDepot.name = data.depot
      this.resetValidation()

      this.setSelectedDepot(data.depot)


      // Save session to sessionStorage for persistence
      this.updateSession()
    },
    endSession(): void {
      this.counter1 = ''
      this.counter2 = ''
      this.enteredArea = ''
      this.selectedDepot.name = ''
      this.resetValidation()

      // Clear session from sessionStorage
      sessionStorage.removeItem('stocktake-session')
    },

    //////////////filters

    resetFilters() {
      Object.assign(this.filters, {
        search: '',
        selectedSubGroup: '',
        selectedPGroup: '',
        selectedArea: '',
        selectedStatus: '',
      })
      this.sortedColumn = null
      this.sortAsc = true
       this.currentPage = 1
      this.pageSize = 25
      // Clear items to force fresh reload when filters are reset
      this.items = []
    },

    // Offline actions
    initializeOfflineService() {
      this.isOnline = offlineService.getIsOnline()
      offlineService.onStatusChange((isOnline) => {
        this.isOnline = isOnline
        if (isOnline) {
          this.resumePolling()
        } else {
          this.pausePolling()
        }
      })
    },

    async updateQueueLength() {
      this.queueLength = await offlineService.getQueueLength()
    },

    async getOfflineQueue() {
      return await offlineService.getQueue()
    },

    async addToOfflineQueue(action: QueuedAction) {
      await offlineService.addToQueue(action)
      await this.updateQueueLength()
    },

    async removeFromOfflineQueue(queueId: string) {
      await offlineService.removeFromQueue(queueId)
      await this.updateQueueLength()
    },

    async setSyncingItem(itemId: string | number, syncing: boolean) {
      if (syncing) {
        this.syncingItemIds.add(itemId)
      } else {
        this.syncingItemIds.delete(itemId)
      }
      await offlineService.setItemSyncStatus(itemId, syncing)
    },

    async clearSyncingItems() {
      this.syncingItemIds.clear()
      await offlineService.clearItemSyncStatus()
    },

    setIsSyncing(syncing: boolean) {
      this.isSyncing = syncing
    },

    addNewOfflineItem(itemId: string | number) {
      this.newOfflineItemIds.add(itemId)
    },

    removeNewOfflineItem(itemId: string | number) {
      this.newOfflineItemIds.delete(itemId)
    },

    clearNewOfflineItems() {
      this.newOfflineItemIds.clear()
    },

    //variance actions

    switchVariance(){
      if(this.filters.selectedStatus === '4' || this.filters.selectedStatus === '5') {
        this.showVariance = true
        
     }else{
        this.showVariance = false
     }
    },


 


    // Helper method to process large arrays in chunks without blocking UI
    // eslint-disable-next-line no-unused-vars
    async filterItemsInChunks<T>(items: T[], filterFn: (item: T) => boolean, chunkSize = 100): Promise<T[]> {
      const results: T[] = []
      
      for (let i = 0; i < items.length; i += chunkSize) {
        const chunk = items.slice(i, i + chunkSize)
        const chunkResults = chunk.filter(filterFn)
        results.push(...chunkResults)
        
        // Yield control to browser periodically
        if (i + chunkSize < items.length) {
          await new Promise(resolve => setTimeout(resolve, 0))
        }
      }
      
      return results
    },
  }
})
